/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2026. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * Models the CodeTableType
 *
 * @class CodeTableType
 */
class CodeTableType {
  /**
   * Creates an instance of CodeTableType.
   * @param {*} json
   * @memberof CodeTableType
   */
  constructor(json = null) {
    if (json) {
      // string
      this.tableName = json.tableName;

      // string
      this.value = json.value;

      // string
      this.description = json.description;

      // string
      this.parentCodeTable = json.parentCodeTable;
    }
  }
}

/**
 * Models the SPMProofType
 *
 * @class SPMProofType
 */
class SPMProofType {
  /**
   * Creates an instance of SPMProofType.
   * @param {*} json
   * @memberof SPMProofType
   */
  constructor(json = null) {
    if (json) {
      // object - CodeTableType
      if (json.name) {
        this.name = new CodeTableType(json.name);
      }

      // object - CodeTableType
      if (json.type) {
        this.type = new CodeTableType(json.type);
      }
    }
  }
}

/**
 * Models the SPMAcceptedProof
 *
 * @class SPMAcceptedProof
 */
class SPMAcceptedProof {
  /**
   * Creates an instance of SPMAcceptedProof.
   * @param {*} json
   * @memberof SPMAcceptedProof
   */
  constructor(json = null) {
    if (json) {
      // string
      this.addedBy = json.addedBy;

      // string
      this.addedDate = json.addedDate;

      // string
      this.provider = json.provider;

      // string
      this.expiryDate = json.expiryDate;

      // object - SPMProofType
      if (json.proofType) {
        this.proofType = new SPMProofType(json.proofType);
      }
    }
  }
}

/**
 * Models the SPMProvidedFile
 *
 * @class SPMProvidedFile
 */
class SPMProvidedFile {
  /**
   * Creates an instance of SPMProvidedFile.
   * @param {*} json
   * @memberof SPMProvidedFile
   */
  constructor(json = null) {
    if (json) {
      // string
      this.linkId = json.linkId;

      // string
      this.fileId = json.fileId;

      // string
      this.receivedDate = json.receivedDate;

      // string
      this.fileName = json.fileName;

      // string
      this.addedBy = json.addedBy;

      // object - CodeTableType
      if (json.name) {
        this.name = new CodeTableType(json.name);
      }
    }
  }
}

/**
 * Models the SPMRelatedCase
 *
 * @class SPMRelatedCase
 */
class SPMRelatedCase {
  /**
   * Creates an instance of SPMRelatedCase.
   * @param {*} json
   * @memberof SPMRelatedCase
   */
  constructor(json = null) {
    if (json) {
      // object - CodeTableType
      if (json.type) {
        this.type = new CodeTableType(json.type);
      }

      // string
      this.id = json.id;

      // string
      this.caseReference = json.caseReference;
    }
  }
}

/**
 * Models the SPMRelatedPerson
 *
 * @class SPMRelatedPerson
 */
class SPMRelatedPerson {
  /**
   * Creates an instance of SPMRelatedPerson.
   * @param {*} json
   * @memberof SPMRelatedPerson
   */
  constructor(json = null) {
    if (json) {
      // string
      this.id = json.id;

      // string
      this.name = json.name;
    }
  }
}

/**
 * Models the SPMRelatedEvidence
 *
 * @class SPMRelatedEvidence
 */
class SPMRelatedEvidence {
  /**
   * Creates an instance of SPMRelatedEvidence.
   * @param {*} json
   * @memberof SPMRelatedEvidence
   */
  constructor(json = null) {
    if (json) {
      // object - CodeTableType
      if (json.type) {
        this.type = new CodeTableType(json.type);
      }

      // string
      this.id = json.id;

      // string
      this.text = json.text;
    }
  }
}

/**
 * Models the SPMRequiredFor
 *
 * @class SPMRequiredFor
 */
class SPMRequiredFor {
  /**
   * Creates an instance of SPMRequiredFor.
   * @param {*} json
   * @memberof SPMRequiredFor
   */
  constructor(json = null) {
    if (json) {
      // object - CodeTableType
      if (json.name) {
        this.name = new CodeTableType(json.name);
      }

      // string
      this.text = json.text;

      // object - SPMRelatedEvidence
      if (json.relatedEvidence) {
        this.relatedEvidence = new SPMRelatedEvidence(json.relatedEvidence);
      }
    }
  }
}

/**
 * Models the SPMGroupReference
 *
 * @class SPMGroupReference
 */
class SPMGroupReference {
  /**
   * Creates an instance of SPMGroupReference.
   * @param {*} json
   * @memberof SPMGroupReference
   */
  constructor(json = null) {
    if (json) {
      // string
      this.id = json.id;
    }
  }
}

/**
 * Models the SPMRequiredProof
 *
 * @class SPMRequiredProof
 */
class SPMRequiredProof {
  /**
   * Creates an instance of SPMRequiredProof.
   * @param {*} json
   * @memberof SPMRequiredProof
   */
  constructor(json = null) {
    if (json) {
      // object - CodeTableType
      if (json.name) {
        this.name = new CodeTableType(json.name);
      }

      // object - CodeTableType
      if (json.type) {
        this.type = new CodeTableType(json.type);
      }

      // boolean
      this.mandatory = json.mandatory;

      // object - CodeTableType
      if (json.usage) {
        this.usage = new CodeTableType(json.usage);
      }

      // boolean
      this.clientProvided = json.clientProvided;

      // string
      this.verificationItemUtilizationId = json.verificationItemUtilizationId;

      // string
      this.verificationItemId = json.verificationItemId;

      // object - SPMGroupReference
      if (json.group) {
        this.group = new SPMGroupReference(json.group);
      }
    }
  }
}

/**
 * Models the SPMWaiver
 *
 * @class SPMWaiver
 */
class SPMWaiver {
  /**
   * Creates an instance of SPMWaiver.
   * @param {*} json
   * @memberof SPMWaiver
   */
  constructor(json = null) {
    if (json) {
      // string
      this.startDate = json.startDate;

      // string
      this.endDate = json.endDate;
    }
  }
}

/**
 * Models the ACESSPMVerification
 *
 * @class ACESSPMVerification
 */
class ACESSPMVerification {
  /**
   * Creates an instance of ACESSPMVerification.
   * @param {*} json
   * @memberof ACESSPMVerification
   */
  constructor(json = null) {
    if (json) {
      // string
      this.verificationId = json.verificationId;

      // object - CodeTableType
      if (json.status) {
        this.status = new CodeTableType(json.status);
      }

      // string
      this.dueDate = json.dueDate;

      // boolean
      this.mandatory = json.mandatory;

      // object - CodeTableType
      if (json.category) {
        this.category = new CodeTableType(json.category);
      }

      // boolean
      this.clientProvided = json.clientProvided;

      // string
      this.programName = json.programName;

      // array - SPMAcceptedProof
      if (json.acceptedProofs) {
        this.acceptedProofs = json.acceptedProofs.map(object => new SPMAcceptedProof(object));
      } else if (json.constructor === Array) {
        this.acceptedProofs = json.map(object => new SPMAcceptedProof(object));
      }

      // array - SPMProvidedFile
      if (json.providedFiles) {
        this.providedFiles = json.providedFiles.map(object => new SPMProvidedFile(object));
      } else if (json.constructor === Array) {
        this.providedFiles = json.map(object => new SPMProvidedFile(object));
      }

      // object - SPMRelatedCase
      if (json.relatedCase) {
        this.relatedCase = new SPMRelatedCase(json.relatedCase);
      }

      // object - SPMRelatedPerson
      if (json.relatedPerson) {
        this.relatedPerson = new SPMRelatedPerson(json.relatedPerson);
      }

      // object - SPMRequiredFor
      if (json.requiredFor) {
        this.requiredFor = new SPMRequiredFor(json.requiredFor);
      }

      // array - SPMRequiredProof
      if (json.requiredProofs) {
        this.requiredProofs = json.requiredProofs.map(object => new SPMRequiredProof(object));
      } else if (json.constructor === Array) {
        this.requiredProofs = json.map(object => new SPMRequiredProof(object));
      }

      // object - SPMWaiver
      if (json.waiver) {
        this.waiver = new SPMWaiver(json.waiver);
      }
    }
  }
}

/**
 * Models the ACESSPMVerificationList
 *
 * @class ACESSPMVerificationList
 */
class ACESSPMVerificationList {
  /**
   * Creates an instance of ACESSPMVerificationList.
   * @param {*} json
   * @memberof ACESSPMVerificationList
   */
  constructor(json = null) {
    if (json) {
      // array - ACESSPMVerification
      if (json.data) {
        this.data = json.data.map(object => new ACESSPMVerification(object));
      } else if (json.constructor === Array) {
        this.data = json.map(object => new ACESSPMVerification(object));
      }
    }
  }
}

/**
 * Models the NewSPMVerificationFileLinkCreation
 *
 * @class NewSPMVerificationFileLinkCreation
 */
class NewSPMVerificationFileLinkCreation {
  /**
   * Creates an instance of NewSPMVerificationFileLinkCreation.
   * @param {*} json
   * @memberof NewSPMVerificationFileLinkCreation
   */
  constructor(json = null) {
    if (json) {
      // string
      this.fileId = json.fileId;

      // string
      this.verificationId = json.verificationId;

      // string
      this.verificationItemUtilizationId = json.verificationItemUtilizationId;
    }
  }
}

/**
 * Models the NewCaseParticipantEvdUploadKey
 *
 * @class NewCaseParticipantEvdUploadKey
 */
class NewCaseParticipantEvdUploadKey {
  /**
   * Creates an instance of NewCaseParticipantEvdUploadKey.
   * @param {*} json
   * @memberof NewCaseParticipantEvdUploadKey
   */
  constructor(json = null) {
    if (json) {
      // string
      this.caseID = json.caseID;

      // boolean
      this.isCaseEvidence = json.isCaseEvidence;

      // string
      this.participantID = json.participantID;

      // string
      this.verificationID = json.verificationID;
    }
  }
}

export default {
  CodeTableType,

  SPMProofType,

  SPMAcceptedProof,

  SPMProvidedFile,

  SPMRelatedCase,

  SPMRelatedPerson,

  SPMRelatedEvidence,

  SPMRequiredFor,

  SPMGroupReference,

  SPMRequiredProof,

  SPMWaiver,

  ACESSPMVerification,

  ACESSPMVerificationList,

  NewSPMVerificationFileLinkCreation,

  NewCaseParticipantEvdUploadKey,
};
